import { Modal } from "flowbite-react";
import { Fragment, useEffect, useRef, useState } from "react";
// Note: getStudentList import removed as student.service is not available for HR management system
import { _GenderData, convertToLabelValue } from "../../../utils/common";
import DefaultSearchSelectInput from "../inputs/defaultSearchSelectInput";
import * as Yup from "yup";
import { useFormik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { _fetchDepartments } from "../../../store/actions/configuration";
// Note: _fetchClasses import removed as class functionality is not available for HR management system
import DefaultButton from "../buttons/defaultButton";
import { PiQuestion } from "react-icons/pi";
import Select from "react-select";
import { getUserList } from "../../../services/workspace/user.service";

const validationSchema = Yup.object().shape({
    department_id: Yup.object(),
    designation_id: Yup.object(),
    gender: Yup.object(),
});

const StaffModal = ({
    pre_value = null,
    show,
    closeModal,
    onSubmit = () => null,
}) => {
    const [selected, setSelected] = useState(null);
    const { departments } = useSelector((state) => state.configuration);
    const [options, setOptions] = useState([]);
    const [designations, setDesignations] = useState([]);
    const dispatch = useDispatch();
    const inputSearchRef = useRef(""); // Track last search term
    const filterChangeTimeoutRef = useRef(null);
    const [tempOptions, setTempOptions] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        dispatch(_fetchDepartments())
    }, []);

    useEffect(() => {
        if (pre_value) {
            setSelected(pre_value);
        }
    }, [pre_value]);

    const { setFieldValue, values } = useFormik({
        initialValues: {
            department_id: "",
            designation_id: "",
            gender: "",
        },
        validationSchema: validationSchema,
        enableReinitialize: true,
        onSubmit: (values) => { },
    });

    const fetchData = async (search = "") => {
        try {
            const params = {
                limit: 20,
                search: search,
                user_type: "staff",
                gender: values.gender?.value || "", // Set as needed what is thee
                designation_id: values.designation_id?.value || "", // Set as needed
                department_id: values.department_id?.value || "", // Set as needed
            };
            return await getUserList(params);
        } catch (error) {
            return [];
        }
    };

    const debounceTimeoutRef = useRef(null); // Store timeout reference

    const loadOptions = async (inputValue) => {
        // Clear previous timeout to ensure only the latest search triggers the API call
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }

        // Set a new timeout to call fetchData after the specified delay
        return new Promise((resolve, reject) => {
            debounceTimeoutRef.current = setTimeout(async () => {
                try {
                    const result = await fetchData(inputValue);
                    if (result && result?.data) {
                        setTempOptions(convertToLabelValue(result.data));
                        setLoading(false);
                        resolve(convertToLabelValue(result.data)); // Return converted data
                    } else {
                        resolve([]); // Return an empty array if no data
                    }
                } catch (error) {
                    reject(error); // Reject if error occurs during API call
                }
            }, 500); // 500ms debounce delay
        });
    };

    const handleOnChange = (value) => {
        setSelected(value);
    };

    useEffect(() => {
        if (!values?.department_id && !values.department_id?.value) return;
        setFieldValue("designation_id", "");
        // Retrieve class data once based on `class_id`
        const foundDesignationData = departments.find(
            (item) => item.value === values.department_id?.value
        );

        // Map sections and subjects if classData exists
        const designationList =
            foundDesignationData?.designations
                .map((item) => ({
                    value: item.id,
                    label: item.name,
                    ...item,
                })) || [];
        setDesignations(designationList);

    }, [values.department_id]);

    const handleReset = () => {
        setFieldValue("designation_id", "");
        setFieldValue("department_id", "");
        setFieldValue("gender", "");
    };

    const handleInputChange = (inputValue) => {
        inputSearchRef.current = inputValue;
        setTempOptions([]);
        if (inputValue) {
            setLoading(true);
            loadOptions(inputValue); // Fetch options based on input
        }
    };

    useEffect(() => {
        if (
            !values.department_id?.value &&
            !values.designation_id?.value &&
            !values.gender?.value
        )
            return;

        if (filterChangeTimeoutRef.current) {
            clearTimeout(filterChangeTimeoutRef.current);
        }

        filterChangeTimeoutRef.current = setTimeout(async () => {
            const searchValue = inputSearchRef.current || "";
            setTempOptions([]);
            setLoading(true);
            try {
                const result = await fetchData(searchValue); // Use the stored input value
                if (result?.data) {
                    const converted = convertToLabelValue(result.data);
                    setTempOptions(converted);
                }
            } catch (error) {
                console.error("Error in debounced filter + input fetch:", error);
            } finally {
                setLoading(false);
            }
        }, 500);

        return () => clearTimeout(filterChangeTimeoutRef.current);
    }, [
        values.gender?.value,
        values.department_id?.value,
        values.designation_id?.value,
    ]);

    const handleSubmitData = () => {
        onSubmit(selected);
        closeModal();
    };

    return (
        <Modal show={show} onClose={closeModal} className={""}>
            <Modal.Header>Staff Search</Modal.Header>
            <Modal.Body>
                <div className={"flex flex-col gap-4 md:gap-8"}>
                    <div className={"grid grid-cols-1 md:grid-cols-2 gap-4"}>
                        <div className="col-span-2 flex justify-start gap-2 items-center">
                            {selected ? (
                                <UserView user={selected} />
                            ) : (
                                <div className="flex justify-center items-center h-12 bg-primary-50 w-full text-sm rounded-md text-primary-500">
                                    No Staff Selected
                                </div>
                            )}
                        </div>

                        <div className="col-span-2">
                            <div className={`flex items-center gap-2`}>
                                <Select
                                    options={tempOptions}
                                    styles={{
                                        menuPortal: (base) => ({
                                            ...base,
                                            fontSize: "12px",
                                            zIndex: 9999,
                                        }),
                                    }}
                                    isLoading={loading} // Display loading indicator
                                    placeholder={"Search By Name and Roll No."}
                                    value={null}
                                    onChange={handleOnChange}
                                    onInputChange={handleInputChange} // Fetch new options on input change
                                    className="flex-1 react-select-container text-sm"
                                    classNamePrefix="react-select"
                                />
                            </div>
                        </div>

                        <div className="col-span-2">
                            <div className="flex justify-between items-center border-b border-primary-100 pb-1">
                                <div className="text-md">Advance Filters</div>
                                <DefaultButton onClick={handleReset} name="Reset" />
                            </div>
                        </div>

                        <Fragment>
                            <DefaultSearchSelectInput
                                label={"Department"}
                                id={"department_id"}
                                name={"department_id"}
                                value={values.department_id}
                                setFieldValue
                                options={departments}
                                onChange={(e) => setFieldValue("department_id", e)}
                            />

                            <DefaultSearchSelectInput
                                label={"Designation"}
                                id={"designation_id"}
                                name={"designation_id"}
                                value={values.designation_id}
                                options={designations}
                                onChange={(e) => setFieldValue("designation_id", e)}
                            />

                            <DefaultSearchSelectInput
                                label={"Gender"}
                                id={"gender"}
                                name={"gender"}
                                value={values.gender}
                                options={_GenderData}
                                onChange={(e) => setFieldValue("gender", e)}
                            />

                        </Fragment>
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <div className="flex justify-start gap-2">
                    <DefaultButton name={"Select"} onClick={handleSubmitData} />
                    <DefaultButton
                        onClick={closeModal}
                        variant="secondary"
                        name={"Close"}
                    />
                </div>
            </Modal.Footer>
        </Modal>
    );
};

const UserView = ({ user }) => {
    return (
        <div className="flex justify-between items-center border border-primary-200 rounded-md h-8 px-2 gap-1 hover:bg-primary-100 cursor-pointer">
            <p className={`text-sm w-auto opacity-100`}>{user.label}</p>
        </div>
    );
};

export default StaffModal;
